import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  downloadStyledExemptionDayReport,
  extractExemptionEmployeesFromWorkbook,
  generateExemptionDayReportFromWorkbook,
  parseDayList,
  REPORT_CODE_DESCRIPTIONS
} from "../helpers/exemptionDayReport";
import {
  EXEMPTION_EXCLUDED_VALUE,
  getAssignmentTemplate,
  saveAssignmentTemplate
} from "../services/exemptionAssignmentService";
import {
  compareExemptionReportWithSgkPdf,
  extractExemptionReportRowsFromWorkbook
} from "../helpers/declarationComparison";

const inputStyle = (isDark) => ({
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: `1px solid ${isDark ? "#2c3d55" : "#dbe2ea"}`,
  background: isDark ? "#111827" : "#fff",
  color: isDark ? "#e5edf6" : "#111827",
  fontWeight: 700,
  boxSizing: "border-box",
  outline: "none"
});

const cardStyle = (isDark) => ({
  background: isDark ? "#172234" : "#fff",
  border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`,
  borderRadius: 18,
  padding: 20,
  boxShadow: isDark ? "0 16px 36px rgba(0,0,0,0.20)" : "0 12px 30px rgba(15,23,42,0.06)"
});

const labelStyle = (isDark) => ({
  display: "block",
  marginBottom: 8,
  color: isDark ? "#cbd5e1" : "#334155",
  fontSize: 13,
  fontWeight: 900
});

const uploadDropStyle = (isDark, accent = "green", disabled = false) => {
  const accents = {
    green: {
      background: isDark ? "#123423" : "#ecfdf5",
      border: isDark ? "#276749" : "#86efac",
      color: isDark ? "#86efac" : "#166534",
      muted: isDark ? "#bbf7d0" : "#15803d"
    },
    blue: {
      background: isDark ? "#0f3148" : "#e0f2fe",
      border: isDark ? "#075985" : "#bae6fd",
      color: isDark ? "#bae6fd" : "#075985",
      muted: isDark ? "#bae6fd" : "#0369a1"
    }
  };
  const palette = accents[accent] || accents.green;

  return {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 220,
    flex: "1 1 240px",
    padding: "14px 16px",
    borderRadius: 16,
    background: palette.background,
    border: `1.5px dashed ${palette.border}`,
    color: palette.color,
    fontWeight: 950,
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.72 : 1,
    transition: "transform 0.15s ease, box-shadow 0.15s ease"
  };
};

const parseExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, {
          type: "array",
          cellDates: false,
          cellStyles: true,
          raw: false
        });

        resolve(workbook);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Excel dosyası okunamadı."));
    reader.readAsArrayBuffer(file);
  });

const getPreviousMonthKey = () => {
  const now = new Date();
  const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  return `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, "0")}`;
};

const getProjectCompanyName = (project = {}) =>
  project.firmaKisaAdi || project.firmaAdi || "Firma";

const getProjectCompanyKey = (project = {}) =>
  String(getProjectCompanyName(project) || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, " ");

const normalizeAssignmentProjects = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item) => item !== EXEMPTION_EXCLUDED_VALUE);
  }

  const cleanValue = String(value || "").trim();
  return cleanValue && cleanValue !== EXEMPTION_EXCLUDED_VALUE ? [cleanValue] : [];
};

const isAssignmentExcluded = (value) => {
  if (Array.isArray(value)) return value.includes(EXEMPTION_EXCLUDED_VALUE);
  return String(value || "").trim() === EXEMPTION_EXCLUDED_VALUE;
};

const isAssignmentUnassigned = (value) =>
  !isAssignmentExcluded(value) && normalizeAssignmentProjects(value).length === 0;

const excelBorder = {
  top: { style: "thin", color: { rgb: "D9E2EC" } },
  bottom: { style: "thin", color: { rgb: "D9E2EC" } },
  left: { style: "thin", color: { rgb: "D9E2EC" } },
  right: { style: "thin", color: { rgb: "D9E2EC" } }
};

const excelStatusStyles = {
  "Uyumlu": { fill: "DCFCE7", font: "166534" },
  "Gün Farkı": { fill: "FEF3C7", font: "92400E" },
  "SGK’da Yok": { fill: "FEE2E2", font: "991B1B" },
  "Bizim Raporda Yok": { fill: "E0F2FE", font: "075985" },
  "Eşleşme Kontrolü": { fill: "F3E8FF", font: "6B21A8" }
};

const applyCellStyle = (sheet, address, style = {}) => {
  if (!sheet[address]) return;
  sheet[address].s = {
    ...(sheet[address].s || {}),
    ...style
  };
};

const applyStyledRange = (sheet, range, getStyle) => {
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const address = XLSX.utils.encode_cell({ r: row, c: col });
      applyCellStyle(sheet, address, getStyle(row, col, sheet[address]));
    }
  }
};

const styleDeclarationWorkbook = (workbook, summarySheet, detailSheet, detailRows) => {
  summarySheet["!cols"] = [{ wch: 30 }, { wch: 55 }];
  detailSheet["!cols"] = [
    { wch: 18 },
    { wch: 28 },
    { wch: 28 },
    { wch: 16 },
    { wch: 16 },
    { wch: 15 },
    { wch: 12 },
    { wch: 10 },
    { wch: 15 }
  ];

  const summaryRange = XLSX.utils.decode_range(summarySheet["!ref"]);
  const detailRange = XLSX.utils.decode_range(detailSheet["!ref"]);

  applyStyledRange(summarySheet, summaryRange, (row, col) => ({
    border: excelBorder,
    alignment: { vertical: "center", wrapText: true },
    font: {
      bold: col === 0,
      color: { rgb: col === 0 ? "0F172A" : "334155" }
    },
    fill: {
      fgColor: { rgb: row <= 3 ? "E0F2FE" : col === 0 ? "F8FAFC" : "FFFFFF" }
    }
  }));

  applyStyledRange(detailSheet, detailRange, (row, col) => {
    if (row === 0) {
      return {
        border: excelBorder,
        fill: { fgColor: { rgb: "124254" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true }
      };
    }

    const status = detailRows[row - 1]?.["Durum"] || "";
    const statusStyle = excelStatusStyles[status] || { fill: row % 2 ? "FFFFFF" : "F8FAFC", font: "334155" };

    return {
      border: excelBorder,
      fill: { fgColor: { rgb: col === 0 ? statusStyle.fill : row % 2 ? "FFFFFF" : "F8FAFC" } },
      font: {
        bold: col === 0,
        color: { rgb: col === 0 ? statusStyle.font : "0F172A" }
      },
      alignment: {
        horizontal: col >= 5 ? "center" : "left",
        vertical: "center",
        wrapText: col < 3
      }
    };
  });

  detailSheet["!autofilter"] = { ref: detailSheet["!ref"] };
  detailSheet["!freeze"] = { xSplit: 0, ySplit: 1 };
  summarySheet["!freeze"] = { xSplit: 0, ySplit: 1 };

  workbook.Workbook = {
    ...(workbook.Workbook || {}),
    Sheets: [
      { name: "Özet", Hidden: 0 },
      { name: "Detay", Hidden: 0 }
    ]
  };
};

export default function ExemptionDayReportView({ projects = [], themeMode = "light" }) {
  const isDark = themeMode === "dark";
  const [periodKey, setPeriodKey] = useState(getPreviousMonthKey);
  const [projectCode, setProjectCode] = useState("");
  const [officialHolidayText, setOfficialHolidayText] = useState("");
  const [bayramHolidayText, setBayramHolidayText] = useState("");
  const [specialCodeText, setSpecialCodeText] = useState("");
  const [extraPersonNotes, setExtraPersonNotes] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [sourceWorkbook, setSourceWorkbook] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState("");
  const [employeeAssignments, setEmployeeAssignments] = useState({});
  const [templateMessage, setTemplateMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [declarationReportFileName, setDeclarationReportFileName] = useState("");
  const [declarationReportRows, setDeclarationReportRows] = useState([]);
  const [sgkDeclarationFileName, setSgkDeclarationFileName] = useState("");
  const [declarationComparison, setDeclarationComparison] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isComparingDeclaration, setIsComparingDeclaration] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const activeProjects = useMemo(() =>
    projects.filter((project) =>
      String(project.projeStbKodu || "").trim() &&
      !String(project.sonlandirmaTarihi || "").trim()
    ),
  [projects]);

  const companyOptions = useMemo(() => {
    const map = new Map();

    activeProjects.forEach((project) => {
      const key = getProjectCompanyKey(project);

      if (!key) return;

      const existing = map.get(key) || {
        key,
        name: getProjectCompanyName(project),
        projects: []
      };

      existing.projects.push(project);
      map.set(key, existing);
    });

    return Array.from(map.values())
      .map((company) => ({
        ...company,
        projects: company.projects.sort((a, b) =>
          String(a.projeStbKodu || "").localeCompare(String(b.projeStbKodu || ""), "tr")
        )
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [activeProjects]);

  const selectedCompany = useMemo(() =>
    companyOptions.find((company) => company.key === selectedCompanyKey) || null,
  [companyOptions, selectedCompanyKey]);

  const selectedCompanyProjects = selectedCompany?.projects || [];
  const needsAssignment = Boolean(sourceWorkbook && selectedCompany && selectedCompanyProjects.length > 1);

  const unassignedEmployees = useMemo(() => {
    if (!needsAssignment) return [];

    return employeeList.filter((employee) =>
      isAssignmentUnassigned(employeeAssignments[employee.key])
    );
  }, [employeeAssignments, employeeList, needsAssignment]);

  const projectCodeOptions = useMemo(() => {
    const map = new Map();

    activeProjects.forEach((project) => {
      const code = String(project.projeStbKodu || "").trim();

      if (!code) return;

      map.set(code, {
        code,
        projectName: project.projeAdi || "",
        label: `${code} — ${project.firmaKisaAdi || project.firmaAdi || "Firma"} / ${project.projeAdi || "Proje"}`
      });
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [activeProjects]);

  const selectedProjectOption = useMemo(() => {
    const cleanCode = String(projectCode || "").trim();

    if (!cleanCode) return null;

    return projectCodeOptions.find((option) => option.code === cleanCode) || null;
  }, [projectCode, projectCodeOptions]);

  const declarationSourceRows = useMemo(() => {
    if (declarationReportRows.length > 0) return declarationReportRows;
    return generatedReport?.rows || [];
  }, [declarationReportRows, generatedReport]);

  const declarationSourceLabel = declarationReportRows.length > 0
    ? "Yüklenen beyan raporu Excel’i"
    : generatedReport?.rows?.length
    ? "Bu ekranda hesaplanan rapor"
    : "Henüz beklenen 5746 kaynağı yok";

  useEffect(() => {
    let isMounted = true;

    const loadTemplate = async () => {
      if (!selectedCompanyKey) {
        setEmployeeAssignments({});
        setTemplateMessage("");
        return;
      }

      setIsTemplateLoading(true);
      setTemplateMessage("");

      try {
        const template = await getAssignmentTemplate(selectedCompanyKey);

        if (!isMounted) return;

        setEmployeeAssignments(template?.assignments || {});
        setTemplateMessage(template ? "Kayıtlı dağılım şablonu uygulandı." : "Bu firma için kayıtlı dağılım şablonu yok.");
      } catch (error) {
        if (!isMounted) return;

        setEmployeeAssignments({});
        setTemplateMessage(`Şablon yüklenemedi: ${error.message}`);
      } finally {
        if (isMounted) {
          setIsTemplateLoading(false);
        }
      }
    };

    loadTemplate();

    return () => {
      isMounted = false;
    };
  }, [selectedCompanyKey]);

  const toggleEmployeeProjectAssignment = (employeeKey, projectCodeValue, checked) => {
    const cleanCode = String(projectCodeValue || "").trim();

    if (!cleanCode) return;

    setEmployeeAssignments((current) => {
      const projectCodes = normalizeAssignmentProjects(current[employeeKey]);
      const nextProjectCodes = checked
        ? Array.from(new Set([...projectCodes, cleanCode]))
        : projectCodes.filter((code) => code !== cleanCode);

      return {
        ...current,
        [employeeKey]: nextProjectCodes
      };
    });
  };

  const toggleEmployeeExcludedAssignment = (employeeKey, checked) => {
    setEmployeeAssignments((current) => ({
      ...current,
      [employeeKey]: checked ? EXEMPTION_EXCLUDED_VALUE : []
    }));
  };

  const saveCurrentAssignments = async () => {
    if (!selectedCompany) {
      setErrorMessage("Şablon kaydetmek için firma seçin.");
      return;
    }

    setIsTemplateSaving(true);
    setErrorMessage("");
    setTemplateMessage("");

    try {
      const nextAssignments = employeeList.reduce((acc, employee) => {
        const value = employeeAssignments[employee.key];

        if (isAssignmentExcluded(value)) {
          acc[employee.key] = EXEMPTION_EXCLUDED_VALUE;
          return acc;
        }

        const projectCodes = normalizeAssignmentProjects(value);

        if (projectCodes.length > 0) {
          acc[employee.key] = projectCodes;
        }

        return acc;
      }, {});

      await saveAssignmentTemplate({
        companyKey: selectedCompany.key,
        companyName: selectedCompany.name,
        assignments: nextAssignments
      });
      setEmployeeAssignments(nextAssignments);
      setTemplateMessage("Dağılım şablonu kaydedildi.");
    } catch (error) {
      setTemplateMessage(`Şablon kaydedilemedi: ${error.message}`);
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const handleSelectedFile = async (file) => {
    if (!file) return;

    setErrorMessage("");
    setGeneratedReport(null);
    setDeclarationComparison(null);
    setSgkDeclarationFileName("");
    setSelectedFileName(file.name);

    try {
      const workbook = await parseExcelFile(file);
      const employees = extractExemptionEmployeesFromWorkbook(workbook);

      setSourceWorkbook(workbook);
      setEmployeeList(employees);

      if (employees.length === 0) {
        setErrorMessage("Excel yüklendi ancak Personel başlığından sonra okunabilir personel satırı bulunamadı.");
      }
    } catch (error) {
      setSourceWorkbook(null);
      setEmployeeList([]);
      setErrorMessage(`Excel okunamadı: ${error.message}`);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    try {
      await handleSelectedFile(file);
    } finally {
      event.target.value = "";
    }
  };

  const handleFileDrop = async (event) => {
    event.preventDefault();
    setIsDraggingFile(false);

    const file = event.dataTransfer.files?.[0];
    await handleSelectedFile(file);
  };

  const generateReport = async () => {
    if (!sourceWorkbook) {
      setErrorMessage("Önce portal sisteminden indirilen dönem çalışma Excel dosyasını yükleyin.");
      return;
    }

    if (!periodKey) {
      setErrorMessage("Dönem seçimi zorunludur.");
      return;
    }

    if (employeeList.length === 0) {
      setErrorMessage("Excel içinde hesaplanacak personel bulunamadı. Personel başlığı ve altındaki satırları kontrol edin.");
      return;
    }

    if (needsAssignment && unassignedEmployees.length > 0) {
      setErrorMessage(`${unassignedEmployees.length} personel için proje veya raporlu/dışarıda seçimi yapılmalı.`);
      return;
    }

    const assignmentProjectCodes = needsAssignment
      ? employeeList.reduce((acc, employee) => {
          const value = employeeAssignments[employee.key];
          const projectCodes = normalizeAssignmentProjects(value);

          if (projectCodes.length > 0) {
            acc[employee.key] = projectCodes;
          }

          return acc;
        }, {})
      : null;
    const includedEmployeeKeys = assignmentProjectCodes
      ? Object.keys(assignmentProjectCodes)
      : null;
    const reportProjectCode = needsAssignment ? "" : projectCode;
    const reportProjectName = needsAssignment ? "" : (selectedProjectOption?.projectName || "");
    const activeProjectNotes = needsAssignment
      ? selectedCompanyProjects
          .map((project) => {
            const code = String(project.projeStbKodu || "").trim();
            const name = String(project.projeAdi || "").trim();

            if (!code && !name) return "";

            return name ? `${code}: ${name}` : `${code}: Proje adı`;
          })
          .filter(Boolean)
      : [];

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const report = generateExemptionDayReportFromWorkbook({
        workbook: sourceWorkbook,
        periodKey,
        projectCode: reportProjectCode,
        projectName: reportProjectName,
        officialHolidayDays: parseDayList(officialHolidayText),
        bayramHolidayDays: parseDayList(bayramHolidayText),
        specialCodeText,
        extraPersonNotes,
        includedEmployeeKeys,
        assignmentProjectCodes,
        activeProjectNotes
      });

      setGeneratedReport(report);
      setDeclarationComparison(null);
      setSgkDeclarationFileName("");
    } catch (error) {
      setGeneratedReport(null);
      setDeclarationComparison(null);
      setErrorMessage(`Rapor oluşturulamadı: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const processSgkDeclarationFile = async (file) => {
    if (!file) return;

    if (!declarationSourceRows.length) {
      setErrorMessage("SGK kontrolü için önce bizim oluşturduğumuz beyan raporu Excel’ini yükleyin veya bu ekranda raporu hesaplayın.");
      return;
    }

    setIsComparingDeclaration(true);
    setErrorMessage("");
    setSgkDeclarationFileName(file.name || "");

    try {
      const comparison = await compareExemptionReportWithSgkPdf({
        file,
        reportRows: declarationSourceRows
      });

      setDeclarationComparison(comparison);
    } catch (error) {
      setDeclarationComparison(null);
      setErrorMessage(`Beyan sonrası kontrol yapılamadı: ${error.message}`);
    } finally {
      setIsComparingDeclaration(false);
    }
  };

  const handleSgkDeclarationFileChange = async (event) => {
    const file = event.target.files?.[0];
    await processSgkDeclarationFile(file);
    event.target.value = "";
  };

  const handleSgkDeclarationDrop = async (event) => {
    event.preventDefault();
    if (isComparingDeclaration) return;
    const file = event.dataTransfer.files?.[0];
    await processSgkDeclarationFile(file);
  };

  const processDeclarationReportFile = async (file) => {
    if (!file) return;

    setErrorMessage("");
    setDeclarationComparison(null);
    setDeclarationReportFileName(file.name || "");

    try {
      const workbook = await parseExcelFile(file);
      const rows = extractExemptionReportRowsFromWorkbook(workbook);

      if (rows.length === 0) {
        throw new Error("Excel içinde Personel ve 5746 Gün Sayısı kolonları okunamadı.");
      }

      setDeclarationReportRows(rows);
    } catch (error) {
      setDeclarationReportRows([]);
      setDeclarationReportFileName("");
      setErrorMessage(`Beyan raporu Excel’i okunamadı: ${error.message}`);
    }
  };

  const handleDeclarationReportFileChange = async (event) => {
    const file = event.target.files?.[0];
    await processDeclarationReportFile(file);
    event.target.value = "";
  };

  const handleDeclarationReportDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    await processDeclarationReportFile(file);
  };

  const downloadDeclarationComparison = async () => {
    if (!declarationComparison?.rows?.length) {
      setErrorMessage("İndirilecek beyan kontrol sonucu bulunamadı.");
      return;
    }

    const styledXlsxModule = await import("xlsx-js-style");
    const styledXlsx = styledXlsxModule.default || styledXlsxModule;
    const workbook = styledXlsx.utils.book_new();
    const summaryRows = [
      ["Firma", declarationComparison.meta.companyName || ""],
      ["Dönem", declarationComparison.meta.period || periodKey],
      ["Kanun", declarationComparison.meta.law || ""],
      ["Onay Tarihi", declarationComparison.meta.approvalDate || ""],
      ["Bizim Toplam 5746 Gün", declarationComparison.summary.expectedTotal],
      ["SGK Toplam Gün", declarationComparison.summary.sgkTotal],
      ["Uyumlu", declarationComparison.summary.matched],
      ["Gün Farkı", declarationComparison.summary.dayMismatch],
      ["SGK’da Yok", declarationComparison.summary.missing],
      ["Bizim Raporda Yok", declarationComparison.summary.extra],
      ["Eşleşme Kontrolü", declarationComparison.summary.uncertain]
    ];
    const detailRows = declarationComparison.rows.map((row) => ({
      "Durum": row.statusLabel,
      "Bizim Personel": row.expectedName,
      "SGK Personel": row.sgkName,
      "Bizim TCKN": row.expectedIdentityNo,
      "SGK No": row.sgkIdentityNo,
      "Bizim 5746 Gün": row.expectedDays ?? "",
      "SGK Gün": row.sgkDays ?? "",
      "Fark": row.difference,
      "Eşleşme Skoru": row.matchScore
    }));

    const summarySheet = styledXlsx.utils.aoa_to_sheet(summaryRows);
    const detailSheet = styledXlsx.utils.json_to_sheet(detailRows);

    styleDeclarationWorkbook(workbook, summarySheet, detailSheet, detailRows);
    styledXlsx.utils.book_append_sheet(workbook, summarySheet, "Özet");
    styledXlsx.utils.book_append_sheet(workbook, detailSheet, "Detay");
    styledXlsx.writeFile(workbook, `5746_Beyan_Kontrol_${periodKey || "donem"}.xlsx`);
  };

  const downloadReport = async () => {
    if (!generatedReport?.workbook) {
      setErrorMessage("İndirilecek rapor bulunamadı. Önce raporu oluşturun.");
      return;
    }

    setIsDownloading(true);
    setErrorMessage("");

    try {
      await downloadStyledExemptionDayReport(generatedReport);
    } catch (error) {
      setErrorMessage(`Excel indirilemedi: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const resetReportForm = () => {
    setProjectCode("");
    setOfficialHolidayText("");
    setBayramHolidayText("");
    setSpecialCodeText("");
    setExtraPersonNotes("");
    setSelectedFileName("");
    setSourceWorkbook(null);
    setGeneratedReport(null);
    setDeclarationReportRows([]);
    setDeclarationReportFileName("");
    setDeclarationComparison(null);
    setSgkDeclarationFileName("");
    setEmployeeList([]);
    setSelectedCompanyKey("");
    setEmployeeAssignments({});
    setTemplateMessage("");
    setErrorMessage("");
    setIsDraggingFile(false);
  };

  const summaryTotals = useMemo(() => {
    if (!generatedReport?.rows) {
      return {
        employees: 0,
        gun5746: 0,
        gun5510: 0,
        reportDays: 0,
        izinDays: 0
      };
    }

    return generatedReport.rows.reduce((acc, row) => ({
      employees: acc.employees + 1,
      gun5746: acc.gun5746 + Number(row.totals.gun5746 || 0),
      gun5510: acc.gun5510 + Number(row.totals.gun5510 || 0),
      reportDays: acc.reportDays + Number(row.totals.reportDays || 0),
      izinDays: acc.izinDays + Number(row.totals.oneYearMissingLeaveDays || 0)
    }), {
      employees: 0,
      gun5746: 0,
      gun5510: 0,
      reportDays: 0,
      izinDays: 0
    });
  }, [generatedReport]);

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: 0, color: isDark ? "#e5edf6" : "#124254" }}>
          Muafiyetli Gün Sayısı Raporu
        </h2>
        <p style={{ margin: "6px 0 0", color: isDark ? "#94a3b8" : "#64748b", fontWeight: 700 }}>
          Portal dönem çalışma Excel&apos;ini yükleyip 5746 / 5510 / rapor günlerini otomatik hesaplayın.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 18, alignItems: "start" }}>
        <div style={cardStyle(isDark)}>
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={labelStyle(isDark)}>Dönem</label>
              <input
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                style={inputStyle(isDark)}
              />
            </div>

            <div>
              <label style={labelStyle(isDark)}>Firma Dağılımı</label>
              <select
                value={selectedCompanyKey}
                onChange={(event) => setSelectedCompanyKey(event.target.value)}
                style={inputStyle(isDark)}
              >
                <option value="">Firma seçmeden devam et</option>
                {companyOptions.map((company) => (
                  <option key={company.key} value={company.key}>
                    {company.name} ({company.projects.length} proje)
                  </option>
                ))}
              </select>
              {isTemplateLoading && (
                <div style={{ marginTop: 6, color: isDark ? "#93c5fd" : "#2563eb", fontWeight: 800, fontSize: 12 }}>
                  Dağılım şablonu yükleniyor...
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle(isDark)}>Proje Kodu</label>
              <select
                value={projectCode}
                onChange={(event) => setProjectCode(event.target.value)}
                style={inputStyle(isDark)}
              >
                <option value="">Proje seçin</option>
                {(selectedCompanyProjects.length > 0
                  ? selectedCompanyProjects.map((project) => ({
                      code: String(project.projeStbKodu || "").trim(),
                      label: `${project.projeStbKodu} — ${project.projeAdi || "Proje"}`
                    }))
                  : projectCodeOptions
                ).map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedProjectOption?.projectName && (
                <div style={{ marginTop: 6, color: isDark ? "#93c5fd" : "#2563eb", fontWeight: 800, fontSize: 12 }}>
                  {projectCode}:{selectedProjectOption.projectName}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={labelStyle(isDark)}>Resmi Tatil Günleri</label>
              <input
                value={officialHolidayText}
                onChange={(event) => setOfficialHolidayText(event.target.value)}
                placeholder="Örn: 1, 19"
                style={inputStyle(isDark)}
              />
            </div>

            <div>
              <label style={labelStyle(isDark)}>Bayram Tatili Günleri</label>
              <input
                value={bayramHolidayText}
                onChange={(event) => setBayramHolidayText(event.target.value)}
                placeholder="Örn: 27, 28, 29, 30"
                style={inputStyle(isDark)}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle(isDark)}>Özel Kodlar</label>
            <textarea
              value={specialCodeText}
              onChange={(event) => setSpecialCodeText(event.target.value)}
              placeholder={"Her satıra: Personel;Gün veya gün aralığı;Kod\nÖrn: BEGÜM YARCI;15;R\nÖrn: EMİRCAN YIKICI;1-20;R\nÖrn: AYŞE YILMAZ;5, 8, 12;İ\nÖrn: MEHMET KAYA;18;YG"}
              rows={6}
              style={{
                ...inputStyle(isDark),
                resize: "vertical",
                lineHeight: 1.5,
                fontFamily: "inherit"
              }}
            />
          </div>

          {needsAssignment && (
            <div style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 14,
              background: isDark ? "#101827" : "#f8fafc",
              border: `1px solid ${unassignedEmployees.length > 0 ? "#f59e0b" : (isDark ? "#26364d" : "#e5e7eb")}`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ color: isDark ? "#e5edf6" : "#124254", fontWeight: 950 }}>
                    Personel Proje Dağılımı
                  </div>
                  <div style={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800, fontSize: 12 }}>
                    {employeeList.length} personel · {selectedCompanyProjects.length} aktif proje · {unassignedEmployees.length} atanmamış
                  </div>
                </div>
                <button
                  onClick={saveCurrentAssignments}
                  disabled={isTemplateSaving || employeeList.length === 0}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #0f766e",
                    background: "#0f766e",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: isTemplateSaving ? "wait" : "pointer",
                    opacity: isTemplateSaving ? 0.7 : 1
                  }}
                >
                  {isTemplateSaving ? "Kaydediliyor..." : "Dağılımı Şablona Kaydet"}
                </button>
              </div>

              {templateMessage && (
                <div style={{
                  marginBottom: 10,
                  color: templateMessage.includes("yüklenemedi") || templateMessage.includes("kaydedilemedi")
                    ? "#b91c1c"
                    : (isDark ? "#93c5fd" : "#2563eb"),
                  fontWeight: 800,
                  fontSize: 12
                }}>
                  {templateMessage}
                </div>
              )}

              {unassignedEmployees.length > 0 && (
                <div style={{
                  marginBottom: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: isDark ? "#3a2a12" : "#fffbeb",
                  border: `1px solid ${isDark ? "#854d0e" : "#fcd34d"}`,
                  color: isDark ? "#facc15" : "#92400e",
                  fontWeight: 900,
                  fontSize: 13
                }}>
                  Yeni/atanmamış personel var. Rapor oluşturmak için her personel için proje veya “Raporlu/Dışarıda” seçin.
                </div>
              )}

              <div style={{
                display: "grid",
                gap: 10,
                maxHeight: 360,
                overflow: "auto",
                paddingRight: 4
              }}>
                {employeeList.map((employee) => {
                  const assignmentValue = employeeAssignments[employee.key];
                  const selectedProjectCodes = normalizeAssignmentProjects(assignmentValue);
                  const isExcluded = isAssignmentExcluded(assignmentValue);
                  const isUnassigned = isAssignmentUnassigned(assignmentValue);

                  return (
                    <div
                      key={employee.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(180px, 0.9fr) minmax(320px, 1.6fr)",
                        gap: 10,
                        alignItems: "stretch",
                        padding: 8,
                        borderRadius: 12,
                        background: isUnassigned ? (isDark ? "#2a1d12" : "#fff7ed") : (isDark ? "#172234" : "#fff"),
                        border: `1px solid ${isUnassigned ? "#f59e0b" : (isDark ? "#26364d" : "#e5e7eb")}`
                      }}
                    >
                      <div style={{
                        padding: "9px 10px",
                        borderRadius: 10,
                        background: isDark ? "#101827" : "#ffffff",
                        color: isDark ? "#e5edf6" : "#334155",
                        fontWeight: 850,
                        fontSize: 13,
                        border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`
                      }}>
                        {employee.personel}
                        {selectedProjectCodes.length > 1 && (
                          <div style={{ marginTop: 4, color: isDark ? "#93c5fd" : "#2563eb", fontSize: 11, fontWeight: 900 }}>
                            {selectedProjectCodes.length} proje aynı satırda yazılacak
                          </div>
                        )}
                      </div>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                        gap: 8,
                        alignItems: "center"
                      }}>
                        {selectedCompanyProjects.map((project) => {
                          const code = String(project.projeStbKodu || "").trim();
                          const checked = selectedProjectCodes.includes(code);

                          return (
                            <label
                              key={project.id || code}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 9px",
                                borderRadius: 10,
                                background: checked ? (isDark ? "#0f3148" : "#e0f2fe") : (isDark ? "#101827" : "#ffffff"),
                                border: `1px solid ${checked ? "#38bdf8" : (isDark ? "#26364d" : "#e5e7eb")}`,
                                color: isDark ? "#e5edf6" : "#334155",
                                fontWeight: 850,
                                fontSize: 12,
                                cursor: "pointer"
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isExcluded}
                                onChange={(event) => toggleEmployeeProjectAssignment(employee.key, code, event.target.checked)}
                              />
                              <span>
                                {code}
                                <span style={{ display: "block", opacity: 0.75, fontWeight: 700 }}>
                                  {project.projeAdi || "Proje"}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                        <label style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 9px",
                          borderRadius: 10,
                          background: isExcluded ? (isDark ? "#3a2a12" : "#fef3c7") : (isDark ? "#101827" : "#ffffff"),
                          border: `1px solid ${isExcluded ? "#f59e0b" : (isDark ? "#26364d" : "#e5e7eb")}`,
                          color: isDark ? "#e5edf6" : "#334155",
                          fontWeight: 850,
                          fontSize: 12,
                          cursor: "pointer"
                        }}>
                          <input
                            type="checkbox"
                            checked={isExcluded}
                            onChange={(event) => toggleEmployeeExcludedAssignment(employee.key, event.target.checked)}
                          />
                          Raporlu / Dışarıda
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle(isDark)}>Excel Alt Notları</label>
            <textarea
              value={extraPersonNotes}
              onChange={(event) => setExtraPersonNotes(event.target.value)}
              placeholder={"Proje adının altına eklenecek ek personel bilgileri.\nÖrn: Ahmet Yılmaz - 01-30 arası raporlu\nÖrn: Ayşe Demir - ayın tamamı ücretsiz izin"}
              rows={4}
              style={{
                ...inputStyle(isDark),
                resize: "vertical",
                lineHeight: 1.5,
                fontFamily: "inherit"
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center"
            }}
          >
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={handleFileDrop}
              style={{
                padding: "14px 18px",
                borderRadius: 12,
                background: isDraggingFile
                  ? (isDark ? "#164e63" : "#e0f2fe")
                  : (isDark ? "#123423" : "#ecfdf5"),
                border: `1px dashed ${isDraggingFile ? "#0284c7" : (isDark ? "#276749" : "#86efac")}`,
                color: isDark ? "#86efac" : "#166534",
                fontWeight: 900,
                cursor: "pointer",
                minWidth: 230,
                textAlign: "center"
              }}
            >
              Excel Yükle veya Sürükle Bırak
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: "none" }} />
            </label>

            <button
              onClick={generateReport}
              disabled={isProcessing}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "1px solid #124254",
                background: "#124254",
                color: "#fff",
                fontWeight: 900,
                cursor: isProcessing ? "wait" : "pointer",
                opacity: isProcessing ? 0.7 : 1
              }}
            >
              {isProcessing ? "Hesaplanıyor..." : "Raporu Hesapla"}
            </button>

            <button
              onClick={downloadReport}
              disabled={!generatedReport || isDownloading}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${generatedReport && !isDownloading ? "#2563eb" : "#cbd5e1"}`,
                background: generatedReport && !isDownloading ? "#2563eb" : (isDark ? "#1e293b" : "#f8fafc"),
                color: generatedReport && !isDownloading ? "#fff" : (isDark ? "#64748b" : "#94a3b8"),
                fontWeight: 900,
                cursor: generatedReport && !isDownloading ? "pointer" : "not-allowed"
              }}
            >
              {isDownloading ? "Excel hazırlanıyor..." : "Excel İndir"}
            </button>

            <button
              onClick={resetReportForm}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${isDark ? "#475569" : "#cbd5e1"}`,
                background: isDark ? "#1e293b" : "#f8fafc",
                color: isDark ? "#e5edf6" : "#334155",
                fontWeight: 900,
                cursor: "pointer"
              }}
            >
              Yeni Hesaplama
            </button>

            {selectedFileName && (
              <span style={{ color: isDark ? "#cbd5e1" : "#475569", fontWeight: 800, fontSize: 13 }}>
                Yüklendi: {selectedFileName}
              </span>
            )}
          </div>

          {errorMessage && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 14px",
                borderRadius: 12,
                background: isDark ? "#3a1618" : "#fee2e2",
                border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"}`,
                color: isDark ? "#fca5a5" : "#b91c1c",
                fontWeight: 800
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>

        <div style={cardStyle(isDark)}>
          <h3 style={{ marginTop: 0, color: isDark ? "#e5edf6" : "#124254" }}>
            Kodlar ve Hesap Özeti
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
            {[
              ["Personel", summaryTotals.employees],
              ["5746 Toplam", summaryTotals.gun5746],
              ["5510 Toplam", summaryTotals.gun5510],
              ["Rapor", summaryTotals.reportDays]
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: isDark ? "#101827" : "#f8fafc",
                  border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`
                }}
              >
                <div style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 12, fontWeight: 900 }}>{label}</div>
                <div style={{ color: isDark ? "#e5edf6" : "#111827", fontSize: 22, fontWeight: 950 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {REPORT_CODE_DESCRIPTIONS.map(([code, description]) => (
              <div
                key={code}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: isDark ? "#101827" : "#f8fafc",
                  color: isDark ? "#cbd5e1" : "#334155",
                  fontWeight: 800,
                  fontSize: 13
                }}
              >
                <span>{description}</span>
                <span style={{ color: isDark ? "#93c5fd" : "#075985" }}>{code}</span>
              </div>
            ))}
          </div>

          {generatedReport?.warnings?.length > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                background: isDark ? "#2a1d12" : "#fff7ed",
                border: `1px solid ${isDark ? "#7c4a18" : "#fed7aa"}`,
                color: isDark ? "#fdba74" : "#9a3412",
                fontWeight: 800,
                fontSize: 13
              }}
            >
              {generatedReport.warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {generatedReport?.rows?.length > 0 && (
        <div style={{ ...cardStyle(isDark), marginTop: 18 }}>
          <h3 style={{ marginTop: 0, color: isDark ? "#e5edf6" : "#124254" }}>
            Önizleme
          </h3>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  {["Sıra", "Personel", "5746", "5510", "Rapor", "İ", "Ham 5746"].map((header) => (
                    <th
                      key={header}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`,
                        color: isDark ? "#cbd5e1" : "#334155",
                        fontSize: 12
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {generatedReport.rows.slice(0, 12).map((row, index) => (
                  <tr key={`${row.tcKimlikNo}-${row.personel}`}>
                    <td style={previewCellStyle(isDark)}>{index + 1}</td>
                    <td style={previewCellStyle(isDark)}>{row.personel}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.gun5746}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.gun5510 || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.reportDays || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.oneYearMissingLeaveDays || "-"}</td>
                    <td style={previewCellStyle(isDark)}>{row.totals.raw5746}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ ...cardStyle(isDark), marginTop: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0, color: isDark ? "#e5edf6" : "#124254" }}>
                Beyan Sonrası 5746 Kontrolü
              </h3>
              <p style={{ margin: "6px 0 0", color: isDark ? "#94a3b8" : "#64748b", fontWeight: 750 }}>
                Bizim rapor Excel’i ve SGK “Kanun: 05746 - 05510” hizmet listesi PDF’i ile istediğiniz an kontrol yapın.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={downloadDeclarationComparison}
                disabled={!declarationComparison?.rows?.length}
                style={{
                  padding: "11px 14px",
                  borderRadius: 12,
                  border: `1px solid ${declarationComparison?.rows?.length ? "#0f766e" : "#cbd5e1"}`,
                  background: declarationComparison?.rows?.length ? "#0f766e" : (isDark ? "#1e293b" : "#f8fafc"),
                  color: declarationComparison?.rows?.length ? "#fff" : (isDark ? "#64748b" : "#94a3b8"),
                  fontWeight: 950,
                  cursor: declarationComparison?.rows?.length ? "pointer" : "not-allowed"
                }}
              >
                Kontrol Excel’i İndir
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
              <label
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDeclarationReportDrop}
                style={uploadDropStyle(isDark, "green")}
              >
                <span style={{ fontSize: 15 }}>📗 Bizim Rapor Excel’i</span>
                <span style={{ fontSize: 12, opacity: 0.85, fontWeight: 850 }}>
                  Dosyayı buraya sürükleyin veya seçmek için tıklayın
                </span>
                {declarationReportFileName && (
                  <span style={{ marginTop: 3, fontSize: 12, fontWeight: 950 }}>
                    Yüklendi: {declarationReportFileName}
                  </span>
                )}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleDeclarationReportFileChange}
                  style={{ display: "none" }}
                />
              </label>

              <label
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleSgkDeclarationDrop}
                style={uploadDropStyle(isDark, "blue", isComparingDeclaration)}
              >
                <span style={{ fontSize: 15 }}>📄 SGK Hizmet Listesi PDF’i</span>
                <span style={{ fontSize: 12, opacity: 0.85, fontWeight: 850 }}>
                  {isComparingDeclaration ? "PDF okunuyor..." : "Dosyayı buraya sürükleyin veya seçmek için tıklayın"}
                </span>
                {sgkDeclarationFileName && (
                  <span style={{ marginTop: 3, fontSize: 12, fontWeight: 950 }}>
                    Yüklendi: {sgkDeclarationFileName}
                  </span>
                )}
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleSgkDeclarationFileChange}
                  disabled={isComparingDeclaration}
                  style={{ display: "none" }}
                />
              </label>
          </div>

          {sgkDeclarationFileName && (
            <div style={{ marginTop: 10, color: isDark ? "#cbd5e1" : "#475569", fontWeight: 850, fontSize: 13 }}>
              Yüklenen SGK PDF: {sgkDeclarationFileName}
            </div>
          )}

          <div style={{ marginTop: 10, color: isDark ? "#cbd5e1" : "#475569", fontWeight: 850, fontSize: 13 }}>
            Beklenen 5746 kaynağı: {declarationSourceLabel}
            {declarationReportFileName ? ` · ${declarationReportFileName}` : ""}
            {declarationSourceRows.length > 0 ? ` · ${declarationSourceRows.length} personel` : ""}
          </div>

          {!declarationComparison && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 14,
                background: isDark ? "#101827" : "#f8fafc",
                border: `1px dashed ${isDark ? "#334155" : "#cbd5e1"}`,
                color: isDark ? "#94a3b8" : "#64748b",
                fontWeight: 800
              }}
            >
              Kontrol sonucu için önce bizim rapor Excel’ini yükleyin veya bu ekranda raporu hesaplayın; ardından müşteriden gelen SGK hizmet listesi PDF’ini yükleyin.
            </div>
          )}

          {declarationComparison && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(110px, 1fr))", gap: 10, marginTop: 16 }}>
                {[
                  ["Beklenen", declarationComparison.expectedCount, "#2563eb"],
                  ["SGK Satırı", declarationComparison.sgkCount, "#0f766e"],
                  ["Uyumlu", declarationComparison.summary.matched, "#16a34a"],
                  ["Gün Farkı", declarationComparison.summary.dayMismatch, "#dc2626"],
                  ["SGK’da Yok", declarationComparison.summary.missing, "#f97316"],
                  ["Bizde Yok", declarationComparison.summary.extra, "#7c3aed"]
                ].map(([label, value, color]) => (
                  <div
                    key={label}
                    style={{
                      padding: 12,
                      borderRadius: 14,
                      background: isDark ? "#101827" : "#fff",
                      border: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`
                    }}
                  >
                    <div style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 12, fontWeight: 900 }}>{label}</div>
                    <div style={{ color, fontSize: 22, fontWeight: 950 }}>{value}</div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: declarationComparison.summary.expectedTotal === declarationComparison.summary.sgkTotal
                    ? (isDark ? "#123423" : "#ecfdf5")
                    : (isDark ? "#3a1618" : "#fee2e2"),
                  color: declarationComparison.summary.expectedTotal === declarationComparison.summary.sgkTotal
                    ? (isDark ? "#86efac" : "#166534")
                    : (isDark ? "#fca5a5" : "#b91c1c"),
                  fontWeight: 950
                }}
              >
                Toplam kontrol: Bizim 5746 toplam {declarationComparison.summary.expectedTotal} gün · SGK toplam {declarationComparison.summary.sgkTotal} gün
                {declarationComparison.meta.period ? ` · SGK dönem ${declarationComparison.meta.period}` : ""}
                {declarationComparison.meta.law ? ` · Kanun ${declarationComparison.meta.law}` : ""}
              </div>

              <div style={{ overflowX: "auto", marginTop: 14 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                  <thead>
                    <tr>
                      {["Durum", "Bizim Personel", "SGK Personel", "Bizim 5746", "SGK Gün", "Fark", "Skor"].map((header) => (
                        <th
                          key={header}
                          style={{
                            textAlign: "left",
                            padding: "10px 12px",
                            borderBottom: `1px solid ${isDark ? "#26364d" : "#e5e7eb"}`,
                            color: isDark ? "#cbd5e1" : "#334155",
                            fontSize: 12
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {declarationComparison.rows.map((row, index) => {
                      const statusStyle = getDeclarationStatusStyle(row.status, isDark);

                      return (
                        <tr key={`${row.expectedIdentityNo}-${row.sgkIdentityNo}-${index}`}>
                          <td style={previewCellStyle(isDark)}>
                            <span
                              style={{
                                display: "inline-flex",
                                padding: "5px 8px",
                                borderRadius: 999,
                                background: statusStyle.background,
                                color: statusStyle.color,
                                fontWeight: 950,
                                fontSize: 12
                              }}
                            >
                              {row.statusLabel}
                            </span>
                          </td>
                          <td style={previewCellStyle(isDark)}>{row.expectedName || "-"}</td>
                          <td style={previewCellStyle(isDark)}>{row.sgkName || "-"}</td>
                          <td style={previewCellStyle(isDark)}>{row.expectedDays ?? "-"}</td>
                          <td style={previewCellStyle(isDark)}>{row.sgkDays ?? "-"}</td>
                          <td style={{ ...previewCellStyle(isDark), color: Number(row.difference || 0) === 0 ? (isDark ? "#86efac" : "#166534") : "#dc2626" }}>
                            {row.difference ?? "-"}
                          </td>
                          <td style={previewCellStyle(isDark)}>{row.matchScore ? `%${row.matchScore}` : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </div>
    </div>
  );
}

const previewCellStyle = (isDark) => ({
  padding: "10px 12px",
  borderBottom: `1px solid ${isDark ? "#26364d" : "#eef2f7"}`,
  color: isDark ? "#e5edf6" : "#111827",
  fontWeight: 700,
  fontSize: 13
});

const getDeclarationStatusStyle = (status, isDark) => {
  const styles = {
    matched: {
      background: isDark ? "#123423" : "#dcfce7",
      color: isDark ? "#86efac" : "#166534"
    },
    dayMismatch: {
      background: isDark ? "#3a1618" : "#fee2e2",
      color: isDark ? "#fca5a5" : "#b91c1c"
    },
    missing: {
      background: isDark ? "#3a2a12" : "#ffedd5",
      color: isDark ? "#fdba74" : "#c2410c"
    },
    extra: {
      background: isDark ? "#2d1b4e" : "#ede9fe",
      color: isDark ? "#c4b5fd" : "#6d28d9"
    },
    uncertain: {
      background: isDark ? "#312e16" : "#fef9c3",
      color: isDark ? "#fde68a" : "#854d0e"
    }
  };

  return styles[status] || {
    background: isDark ? "#1e293b" : "#f1f5f9",
    color: isDark ? "#cbd5e1" : "#475569"
  };
};
